import { DetectionZone, LaneLine, ScreenLine, Device, Facilities } from "@app/shared";
import React, { useState, useEffect, useCallback, useRef } from "react";
import { toastError, toastInfo, dismissToast } from "../../../../../helpers";
import { LayoutDesigner } from "../../../../shared/LayoutDesigner/LayoutDesigner";
import { ShapeFilter } from "../../../../shared/LayoutDesigner/ShapeFilter";
import { useFetch } from "../../../../../hooks";
import { Button } from "@app/shared";
import { Icons } from "../../../../shared";

type MappingShapesStepProps = {
    facilityId: string;
    deviceId: string;
    onContinue: (shapes: Array<DetectionZone | ScreenLine | LaneLine>) => void;
    onBack?: () => void;
    className?: string;
};

export const MappingShapesStep: React.FC<MappingShapesStepProps> = ({ facilityId, deviceId, onContinue, onBack, className }) => {
    const [device, setDevice] = useState<Device | null>(null);
    const [facility, , helpers] = useFetch(() => Facilities.getById(facilityId));
    const [isSaving] = useState(false);
    const confirmToastId = useRef("");

    const shapeFilter = new ShapeFilter({
        showDevicesOnMap: true,
        showSpotsOnMap: true,
        showDetectionZonesOnMap: true,
        showScreenLinesOnMap: true,
        showLaneLinesOnMap: true,
        showReferencePointOnMap: true,
        showDevicesInConfig: false,
        showSpotsInConfig: false,
        showDetectionZonesInConfig: true,
        showScreenLinesInConfig: true,
        showLaneLinesInConfig: true,
        showReferencePointInConfig: false,
    });

    useEffect(() => {
        const loadDevice = async () => {
            try {
                let foundDevice = facility?.devices?.find((d) => d.id === deviceId);
                if (!foundDevice) {
                    throw new Error("Device not found");
                }
                setDevice(foundDevice);
            } catch (error) {
                console.error("Error loading device:", error);
            }
        };

        if (facility && deviceId) {
            loadDevice();
        }
    }, [facility, deviceId]);

    const showNoShapesConfirmation = useCallback(() => {
        if (confirmToastId.current) {
            dismissToast(confirmToastId.current);
            confirmToastId.current = "";
            return;
        }

        confirmToastId.current = toastInfo(
            "No Detection Areas Created",
            <>
                <div className="flex flex-col gap-3">
                    <p className="text-sm">No shapes have been created yet. You can continue without detection areas, but this may limit the device's functionality.</p>
                    <div className="flex gap-2 justify-end">
                        <Button
                            onClick={() => {
                                dismissToast(confirmToastId.current);
                                confirmToastId.current = "";
                            }}
                            className="px-4 py-2 text-sm bg-white border border-warmGray-300 hover:border-warmGray-400 text-neutral-700 rounded-xl transition-all duration-200 hover:bg-warmGray-50"
                        >
                            Go Back
                        </Button>
                        <Button
                            onClick={() => {
                                dismissToast(confirmToastId.current);
                                confirmToastId.current = "";
                                onContinue([]);
                            }}
                            className="px-4 py-2 text-sm bg-brand-primary hover:bg-brand-primary-darkened text-white rounded-xl transition-all duration-200 shadow-lg hover:shadow-xl"
                        >
                            Continue Anyway
                        </Button>
                    </div>
                </div>
            </>,
            0
        );
    }, [onContinue]);

    const handleContinue = useCallback(async () => {
        if (!facility) {
            toastError("Location not loaded");
            return;
        }

        const allShapes: Array<DetectionZone | ScreenLine | LaneLine> = [...(facility.detectionZones || []), ...(facility.screenLines || []), ...(facility.laneLines || [])];

        const completedShapes = allShapes.filter((shape) => shape.isComplete && shape.isComplete());

        if (completedShapes.length === 0) {
            showNoShapesConfirmation();
            return;
        }

        onContinue(completedShapes);
    }, [facility, onContinue, showNoShapesConfirmation]);

    if (!facility) {
        return (
            <div className="flex justify-center items-center h-96">
                <div className="text-center">
                    <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-brand-primary mx-auto mb-4"></div>
                    <p className="text-neutral-600">Loading facility...</p>
                </div>
            </div>
        );
    }

    return (
        <div className={`flex flex-col h-full ${className || ""}`}>
            <div className="bg-gradient-to-r from-warmGray-50 to-white border-b border-warmGray-100 px-8 pt-1 pb-2">
                <div className="flex items-center justify-between">
                    <div className="flex items-center gap-4">
                        <div className="w-12 h-12 bg-gradient-to-br from-brand-primary to-brand-primary-darkened rounded-2xl flex items-center justify-center shadow-lg">
                            <Icons.Pencil className="w-6 h-6 text-white" />
                        </div>
                        <div>
                            <h2 className="text-2xl font-bold text-neutral-900">Shape Mapping</h2>
                            <p className="text-sm text-neutral-600 mt-1">
                                Add detection zones, screen lines, lane lines and parking spots for device: <span className="font-semibold text-neutral-700">{device?.name || deviceId}</span>
                            </p>
                        </div>
                    </div>
                </div>
            </div>

            <div className="bg-gradient-to-r from-terracotta-50 to-warmGray-50 border-b border-brand-primary border-opacity-20 px-8 py-6">
                <div className="flex items-start gap-4">
                    <div className="w-8 h-8 bg-brand-primary rounded-lg flex items-center justify-center flex-shrink-0 mt-0.5">
                        <Icons.Info className="w-4 h-4 text-white" />
                    </div>
                    <div className="flex-1">
                        <h3 className="text-sm font-semibold text-neutral-900 mb-3">How to use:</h3>
                        <div className="text-sm text-neutral-600 space-y-2 leading-relaxed">
                            <p className="flex items-start gap-2">
                                <div className="w-6 h-6 bg-brand-primary rounded-lg flex items-center justify-center flex-shrink-0 mt-0.5">
                                    <span className="text-white font-bold text-xs">1</span>
                                </div>
                                <span>
                                    <span className="font-medium text-neutral-700">Click "Edit Layout"</span> to enter editing mode
                                </span>
                            </p>
                            <p className="flex items-start gap-2">
                                <div className="w-6 h-6 bg-brand-primary rounded-lg flex items-center justify-center flex-shrink-0 mt-0.5">
                                    <span className="text-white font-bold text-xs">2</span>
                                </div>
                                <span>
                                    <span className="font-medium text-neutral-700">Select a tool</span> (Detection Zone, Screen Line, Lane Line, or Parking Spot) from the toolbar
                                </span>
                            </p>
                            <p className="flex items-start gap-2">
                                <div className="w-6 h-6 bg-brand-primary rounded-lg flex items-center justify-center flex-shrink-0 mt-0.5">
                                    <span className="text-white font-bold text-xs">3</span>
                                </div>
                                <span>
                                    <span className="font-medium text-neutral-700">Click on the map</span> to place points and create your shape
                                </span>
                            </p>
                            <p className="flex items-start gap-2">
                                <div className="w-6 h-6 bg-brand-primary rounded-lg flex items-center justify-center flex-shrink-0 mt-0.5">
                                    <span className="text-white font-bold text-xs">4</span>
                                </div>
                                <span>
                                    <span className="font-medium text-neutral-700">Click "Save"</span> immediately after completing each shape
                                </span>
                            </p>
                            <p className="flex items-start gap-2">
                                <div className="w-6 h-6 bg-brand-primary rounded-lg flex items-center justify-center flex-shrink-0 mt-0.5">
                                    <span className="text-white font-bold text-xs">5</span>
                                </div>
                                <span>
                                    <span className="font-medium text-neutral-700">Repeat</span> for additional shapes, saving after each one
                                </span>
                            </p>
                        </div>
                    </div>
                </div>
            </div>

            <div className="flex-1">
                <LayoutDesigner facility={facility} shapeFilter={shapeFilter} onChange={helpers.refreshData} />
            </div>

            <div className="bg-gradient-to-r from-warmGray-50 to-white border-t border-warmGray-100 px-8 py-6">
                <div className="flex items-center justify-between">
                    <div className="flex items-center gap-6">
                        <div className="flex items-center gap-3">
                            <div className="w-8 h-8 bg-brand-primary rounded-lg flex items-center justify-center">
                                <Icons.Target className="w-4 h-4 text-white" />
                            </div>
                            <p className="text-sm text-neutral-600">
                                <span className="font-semibold text-neutral-700">Detection Zones:</span> Define areas for target detection
                            </p>
                        </div>
                        <div className="flex items-center gap-3">
                            <div className="w-8 h-8 bg-brand-primary rounded-lg flex items-center justify-center">
                                <Icons.Grid3x3 className="w-4 h-4 text-white" />
                            </div>
                            <p className="text-sm text-neutral-600">
                                <span className="font-semibold text-neutral-700">Screen/Lane Lines:</span> Set up the trajectory to be detected
                            </p>
                        </div>
                    </div>

                    <div className="flex gap-4">
                        {onBack && (
                            <Button onClick={onBack} className="flex items-center gap-3 px-6 py-3 bg-white border border-warmGray-300 hover:border-warmGray-400 text-neutral-700 font-semibold rounded-xl transition-all duration-200 hover:bg-warmGray-50" disabled={isSaving}>
                                <Icons.ArrowBack className="w-4 h-4" />
                                Back
                            </Button>
                        )}
                        <Button onClick={handleContinue} className="flex items-center gap-3 px-8 py-4 bg-brand-primary hover:bg-brand-primary-darkened text-white font-semibold rounded-xl transition-all duration-200 shadow-lg hover:shadow-xl disabled:opacity-50 disabled:cursor-not-allowed disabled:shadow-none" disabled={isSaving}>
                            {isSaving ? (
                                <>
                                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                                    Saving...
                                </>
                            ) : (
                                <>
                                    Continue
                                    <Icons.ArrowRight className="w-4 h-4" />
                                </>
                            )}
                        </Button>
                    </div>
                </div>
            </div>
        </div>
    );
};
